import * as React from 'react';
import { Query } from '@apollo/client/react/components';

import Icon from 'buyside/components/presentation/Icon/icon';

const AccessProfileCurrentUser = require('buyside/graphql/queries/AccessProfileCurrentUser');
const LastNIdeaViewsCurrentUser = require('buyside/graphql/queries/LastNIdeaViewsCurrentUser');

import Button, {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_WIDTHS
} from 'buyside/components/presentation/Button/button';
import IdeaTokenWidget from 'buyside/components/presentation/IdeaTokenWidget/ideaTokenWidget';
import SidebarWidget from 'buyside/components/presentation/SidebarWidget/sidebarWidget';
import SidebarProfileWidget from 'buyside/components/presentation/SidebarProfileWidget/sidebarProfileWidget';
import SidebarViewsWidget from 'buyside/components/presentation/SidebarViewsWidget/sidebarViewsWidget';
import RankingsWidgetContainer from 'buyside/components/container/RankingsWidget/RankingsWidgetContainer';
import Link from 'buyside/components/presentation/Link/link';
import LoadingSpinner, {
  SIZES,
  TYPES
} from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import ErrorMessage from 'buyside/components/presentation/ErrorMessage/errorMessage';
import UserIcon from 'buyside/svgs/user_icon.svg';

import {
  AccessProfileCurrentUserQuery,
  LastNIdeaViewsCurrentUserQuery
} from 'interfaces/graphql-typings';

import BookIcon from 'buyside/svgs/book.svg';

import { contributorDashboardUrl, userReadingListUrl } from 'buyside/util/urlHelper';

import styles from 'buyside/components/presentation/LeftSidebar/leftSidebarStyles.scss';

const LeftSidebar: React.FC = (): React.ReactElement => {
  return (
    <Query<AccessProfileCurrentUserQuery> query={AccessProfileCurrentUser}>
      {({ loading, data: profileData }): React.ReactElement => {
        if (loading || !profileData) return <LoadingSpinner type={TYPES.SECONDARY} />;
        if (!profileData.currentUser)
          return <ErrorMessage>Something went wrong. Please refresh the page.</ErrorMessage>;

        return (
          <Query<LastNIdeaViewsCurrentUserQuery>
            query={LastNIdeaViewsCurrentUser}
            variables={{ n: 5 }}
          >
            {({ loading, data: viewsData }): React.ReactElement => {
              if (loading || !viewsData) return <LoadingSpinner size={SIZES.MEDIUM} />;
              const showContributorDashboard =
                !viewsData?.currentUser?.ideaIsBlocked &&
                Boolean(viewsData?.currentUser?.publishedIdeasCount);

              return (
                <aside>
                  {profileData.currentUser?.isTokenBased && (
                    <Button
                      text={`Your Unlocked Reports (${profileData.currentUser
                        ?.readingListItemsCount || 0})`}
                      type={BUTTON_TYPES.PRIMARY}
                      size={BUTTON_SIZES.LARGE}
                      url={userReadingListUrl()}
                      width={BUTTON_WIDTHS.FULL}
                      icon={BookIcon}
                    />
                  )}
                  <SidebarWidget>
                    <SidebarProfileWidget data={profileData} />
                  </SidebarWidget>
                  {profileData.currentUser?.isTokenBased && (
                    <SidebarWidget>
                      <IdeaTokenWidget
                        readingListItemsCount={profileData.currentUser?.readingListItemsCount}
                      />
                    </SidebarWidget>
                  )}
                  {showContributorDashboard && (
                    <SidebarWidget>
                      <div className={styles.contributorDashboardHeading}>
                        <Icon className={styles.contributorDashboardHeadingIcon} type={UserIcon} />
                        <Link to={contributorDashboardUrl()}>
                          <p>Contributor Dashboard</p>
                        </Link>
                      </div>
                      <div className={styles.contributorDashboardDescription}>
                        Share new research for continued exposure, monitor viewership stats on
                        existing content, and manage your fund profile - all in one place.
                        <Button
                          text="Go to My Dashboard"
                          url={contributorDashboardUrl()}
                          size={BUTTON_SIZES.TINY}
                          type={BUTTON_TYPES.PRIMARY}
                          width={BUTTON_WIDTHS.FULL}
                        />
                      </div>
                    </SidebarWidget>
                  )}
                  {Boolean(viewsData?.currentUser?.publishedIdeasCount) && (
                    <SidebarWidget>
                      <SidebarViewsWidget data={viewsData} />
                    </SidebarWidget>
                  )}
                  <SidebarWidget noPadding>
                    <RankingsWidgetContainer />
                  </SidebarWidget>
                </aside>
              );
            }}
          </Query>
        );
      }}
    </Query>
  );
};

export default LeftSidebar;
